import { useCallback, useEffect, useState } from 'react';

export type Theme = 'dark' | 'light';

const KEY = 'anyapi-theme-a';
const QUERY = '(prefers-color-scheme: dark)';

/** The OS preference, defaulting to light when it can't be read (SSR/old browsers). */
const systemTheme = (): Theme =>
  typeof window !== 'undefined' && window.matchMedia?.(QUERY).matches ? 'dark' : 'light';

/** True once the user has explicitly picked a theme (vs. following the OS). */
const hasExplicit = (): boolean => {
  try {
    const s = localStorage.getItem(KEY);
    return s === 'light' || s === 'dark';
  } catch {
    return false;
  }
};

const read = (): Theme => {
  // The pre-paint script in __root.tsx already resolved persisted-or-system
  // onto data-theme; mirror it so SSR markup and first client render agree.
  if (typeof document !== 'undefined') {
    const attr = document.documentElement.getAttribute('data-theme');
    if (attr === 'light' || attr === 'dark') return attr;
  }
  return systemTheme();
};

const apply = (theme: Theme, persist: boolean): void => {
  document.documentElement.setAttribute('data-theme', theme);
  if (!persist) return;
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    /* ignore storage failures */
  }
};

/**
 * Reads/writes the theme and toggles `data-theme` on the root. Follows the OS
 * `prefers-color-scheme` (live) until the user toggles, after which their
 * explicit choice persists and wins.
 */
export const useTheme = (): { theme: Theme; toggle: () => void } => {
  const [theme, setTheme] = useState<Theme>(read);

  // Reflect the current theme without re-persisting (avoids clobbering the
  // "following OS" state with an explicit choice on every render).
  useEffect(() => {
    apply(theme, false);
  }, [theme]);

  // Track the OS preference while the user hasn't made an explicit choice.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(QUERY);
    const onChange = () => {
      if (!hasExplicit()) setTheme(mq.matches ? 'dark' : 'light');
    };
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const toggle = useCallback(() => {
    setTheme((t) => {
      const next = t === 'dark' ? 'light' : 'dark';
      apply(next, true);
      return next;
    });
  }, []);

  return { theme, toggle };
};
